import { Component, inject, signal } from '@angular/core';
import { LegalLocalizationService } from '../../services/legal-localization-service';
import { I18nService } from '../../services/i18n.service';
import { NavButtonComponent } from '../nav-button/nav-button';
import { ScrollToTopComponent } from '../scroll-to-top/scrollToTop';
import { Router } from '@angular/router';
import { generateKeys, mapToLatam } from '../../utilities/global.util';
import { App_Routes } from '../../enums/router.enum';
@Component({
  selector: 'app-terms-of-use',
  templateUrl: './terms-of-use.html',
  styleUrls: ['./terms-of-use.scss'],
  imports: [ScrollToTopComponent, NavButtonComponent],
})
export class TermsOfUseComponent {
  lls = inject(LegalLocalizationService);
  i18n = inject(I18nService);
  router = inject(Router);

  public showUSCountryPage = signal(this.lls.checkForUS());
  public showLatamCountryPage = signal(this.lls.checkForUS());

  getCurrentCountryVersion(): string {
    if (this.showUSCountryPage()) return 'US';
    if (this.showLatamCountryPage()) return 'LATAM';
    return 'Default/Global';
  }

  setCountry(country: 'us' | 'latam' | 'default'): void {
    this.showUSCountryPage.set(country === 'us');
    this.showLatamCountryPage.set(country === 'latam');
  }

  gotoLogin() {
    if (this.router.url.includes('settings')) {
      this.router.navigate([App_Routes.SETTINGS]);
    } else {
      this.router.navigate([App_Routes.LOGIN]);
    }
  }

  private defaultScopeKeys = {
    scope1: generateKeys('scope_1', 1, 3),
    scope2: generateKeys('scope_2', 1, 7),
    scope3Main: ['scope_3_1_content', 'scope_3_2_content'],
    scope3Sub1: [
      'scope_3_1_a_content',
      'scope_3_1_b_content',
      'scope_3_1_c_content',
      'scope_3_1_d_content',
      'scope_3_1_e_content',
      'scope_3_1_f_content',
    ],
    scope3Sub2: [
      'scope_3_2_a_content',
      'scope_3_2_b_content',
      'scope_3_2_c_content',
      'scope_3_2_d_content',
      'scope_3_2_e_content',
      'scope_3_2_f_content',
      'scope_3_2_g_content',
      'scope_3_2_h_content',
      'scope_3_2_i_content',
    ],
    scope4: generateKeys('scope_4', 1, 2),
    scope5: generateKeys('scope_5', 1, 3),
    scope6: generateKeys('scope_6', 1, 2),
    scope7: generateKeys('scope_7', 1, 5),
    scope8: generateKeys('scope_8', 1, 3),
    scope9Main: ['scope_9_1_content', 'scope_9_2_content'],
    scope9Sub: [
      'scope_9_1_a_content',
      'scope_9_1_b_content',
      'scope_9_1_c_content',
      'scope_9_1_d_content',
    ],
    scope10: generateKeys('scope_10', 1, 3),
  };

  // Define the structured array used by the @for loop for Default/Global content
  defaultScopes = [
    { key: 1, titleKey: 'scope_1', contentKeys: this.defaultScopeKeys.scope1 },
    { key: 2, titleKey: 'scope_2', contentKeys: this.defaultScopeKeys.scope2 },
    {
      key: 3,
      titleKey: 'scope_3',
      mainContentKeys: this.defaultScopeKeys.scope3Main,
      subpointKeys: this.defaultScopeKeys.scope3Sub1,
      subpointKeys2: this.defaultScopeKeys.scope3Sub2,
    },
    { key: 4, titleKey: 'scope_4', contentKeys: this.defaultScopeKeys.scope4 },
    { key: 5, titleKey: 'scope_5', contentKeys: this.defaultScopeKeys.scope5 },
    { key: 6, titleKey: 'scope_6', contentKeys: this.defaultScopeKeys.scope6 },
    { key: 7, titleKey: 'scope_7', contentKeys: this.defaultScopeKeys.scope7 },
    { key: 8, titleKey: 'scope_8', contentKeys: this.defaultScopeKeys.scope8 },
    {
      key: 9,
      titleKey: 'scope_9',
      mainContentKeys: this.defaultScopeKeys.scope9Main,
      subpointKeys: this.defaultScopeKeys.scope9Sub,
    },
    {
      key: 10,
      titleKey: 'scope_10',
      contentKeys: this.defaultScopeKeys.scope10,
    },
  ];

  // Derive LATAM content arrays
  private latamScopeKeys = {
    scope1: mapToLatam(this.defaultScopeKeys.scope1),
    scope2: mapToLatam(this.defaultScopeKeys.scope2),
    scope3Main: mapToLatam(this.defaultScopeKeys.scope3Main),
    scope3Sub1: mapToLatam(this.defaultScopeKeys.scope3Sub1),
    scope3Sub2: mapToLatam(this.defaultScopeKeys.scope3Sub2),
    scope4: mapToLatam(this.defaultScopeKeys.scope4),
    scope5: mapToLatam(this.defaultScopeKeys.scope5),
    scope6: mapToLatam(this.defaultScopeKeys.scope6),
    scope7: mapToLatam(this.defaultScopeKeys.scope7),
    scope8: mapToLatam(this.defaultScopeKeys.scope8),
    scope9Main: mapToLatam(this.defaultScopeKeys.scope9Main),
    scope9Sub: mapToLatam(this.defaultScopeKeys.scope9Sub),
    scope10: mapToLatam(this.defaultScopeKeys.scope10),
  };

  // Define the structured array used by the @for loop for LATAM content
  latamScopes = [
    {
      key: 1,
      titleKey: 'terms_scope_1_latam',
      contentKeys: this.latamScopeKeys.scope1,
    },
    {
      key: 2,
      titleKey: 'terms_scope_2_latam',
      contentKeys: this.latamScopeKeys.scope2,
    },
    {
      key: 3,
      titleKey: 'terms_scope_3_latam',
      mainContentKeys: this.latamScopeKeys.scope3Main,
      subpointKeys: this.latamScopeKeys.scope3Sub1,
      subpointKeys2: this.latamScopeKeys.scope3Sub2,
    },
    {
      key: 4,
      titleKey: 'terms_scope_4_latam',
      contentKeys: this.latamScopeKeys.scope4,
    },
    {
      key: 5,
      titleKey: 'terms_scope_5_latam',
      contentKeys: this.latamScopeKeys.scope5,
    },
    {
      key: 6,
      titleKey: 'terms_scope_6_latam',
      contentKeys: this.latamScopeKeys.scope6,
    },
    {
      key: 7,
      titleKey: 'terms_scope_7_latam',
      contentKeys: this.latamScopeKeys.scope7,
    },
    {
      key: 8,
      titleKey: 'terms_scope_8_latam',
      contentKeys: this.latamScopeKeys.scope8,
    },
    {
      key: 9,
      titleKey: 'terms_scope_9_latam',
      mainContentKeys: this.latamScopeKeys.scope9Main,
      subpointKeys: this.latamScopeKeys.scope9Sub,
    },
    {
      key: 10,
      titleKey: 'terms_scope_10_latam',
      contentKeys: this.latamScopeKeys.scope10,
    },
  ];

  // --- US Content Arrays (Less repetitive parts) ---
  loctitePulseFunctions = [
    {
      key: 1,
      titleKey: 'map_table_overview',
      contentKey: 'map_table_overview_content',
    },
    { key: 2, titleKey: 'data_display', contentKey: 'data_display_content' },
    {
      key: 3,
      titleKey: 'maintenance_documentation',
      contentKey: 'maintenance_documentation_content',
    },
    {
      key: 4,
      titleKey: 'loctite_pulse_notifications',
      contentKey: 'loctite_pulse_notifications_content',
    },
    {
      key: 5,
      titleKey: 'push_notifications',
      contentKey: 'push_notifications_content',
    },
    {
      key: 6,
      titleKey: 'email_notifications',
      contentKey: 'email_notifications_content',
    },
    {
      key: 7,
      titleKey: 'installation_agent',
      contentKey: 'installation_agent_content',
    },
  ];
}
